import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";
import { SENIOR_ADVISORS } from "@/data/seniorAdvisors";
import { LEADERSHIP } from "@/data/leadership";
import { REGIONAL_COVERAGE } from "@/data/regionalCoverage";

/**
 * /team — expanded team & advisors page (separate from /partnership).
 * Covers co-founders, reserved senior seats, the Investment Committee
 * composition and the Operating Partner Network.
 */

const FOUNDING_PARTNERS = [
  {
    id: "marcel-kellermann",
    initials: "MK",
    name: "Marcel Kellermann",
    role: "Managing Partner",
    focus: "Investor Relations · Capital Formation · Investment Strategy",
    photo: "/portraits/marcel-kellermann.jpg",
  },
  {
    id: "chris-reid",
    initials: "CR",
    name: "Chris Reid",
    role: "Managing Partner",
    focus: "Commercial Finance · Strategy · Capital Partnerships",
    photo: "/portraits/chris-reid.jpg",
  },
];

const PARTNERS = [
  {
    id: "rachel-yang",
    initials: "RY",
    name: "Rachel Yang",
    role: "Partner · Asia-Pacific",
    focus: "Deal Origination · Investor Relations · Asia-Pacific Coverage",
    photo: "/portraits/rachel-yang.jpg",
  },
  {
    id: "ryan-landry",
    initials: "RL",
    name: "Ryan Landry",
    role: "Partner · Strategic Capital",
    focus: "Strategic Capital · Family Offices · Investor Partnerships",
    photo: "/portraits/ryan-landry.jpg",
  },
];

const RESERVED = [
  {
    seal: "III",
    role: "Managing Director · Credit & Structuring",
    profile:
      "Senior credit principal with extensive experience across senior-secured, unitranche and bridge financings.",
  },
  {
    seal: "IV",
    role: "Managing Director · Real Estate Credit",
    profile:
      "Specialist in industrial and operationally critical real estate financing across European and select North American markets.",
  },
  {
    seal: "V",
    role: "Head of Risk, Legal & Compliance",
    profile:
      "Senior risk professional with regulatory, market and operational risk experience.",
  },
  {
    seal: "VI",
    role: "Head of Limited Partner Relations",
    profile:
      "Senior IR background across institutional, family office and placement agent channels.",
  },
];

const NETWORK = [
  {
    title: "Investment Committee",
    summary:
      "Constituted; documented diligence and recorded deliberation underpin every commitment. Independent perspectives are sought before, not after, decision.",
    members: [
      "Co-Founders (×2)",
      "Senior Credit Principal",
      "Senior Equity Principal",
      "Risk Chair (independent vote)",
      "Two external industrial advisors (consultative)",
    ],
  },
  {
    title: "Operating Partner Network",
    summary:
      "A confidentially curated network of senior industrial operators engaged across diligence and value-creation — not employees of the firm.",
    members: [
      "Industrial manufacturing & precision engineering",
      "Logistics & operationally critical real estate",
      "Energy transition & onshoring infrastructure",
      "Specialty chemicals & process industries",
      "Senior workforce, safety & sustainability advisors",
    ],
  },
  {
    title: "Independent Oversight",
    summary:
      "Risk, legal & compliance reports independently of the deal teams. Conflicts management is documented and proactive.",
    members: [
      "Independent Risk Chair",
      "External legal counsel (multi-jurisdictional)",
      "Independent fund administrator",
      "Independent custody & audit counterparties",
      "AML/KYC programme governed by documented standards",
    ],
  },
];

const TeamPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.teamPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Team"
          label="Founders · Investment Committee · Operating Network"
          title={
            <>
              The people behind the{" "}
              <span className="italic text-[var(--kr-gold)]">platform</span>.
            </>
          }
          description="Kellermann Reid & Co. is operated by a senior partnership, supported by a documented Investment Committee, an Operating Partner Network and independent oversight. Investor relations sits outside the deal teams."
          breadcrumb="Team"
        />

        {/* Founders */}
        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            {/* Tier 1 — Founding Partners */}
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Founding Partners
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>
            <div className="grid md:grid-cols-2 gap-10 mb-16">
              {FOUNDING_PARTNERS.map((f) => (
                <article
                  key={f.id}
                  data-testid={`${LANDING.teamPagePartnerPrefix}${f.id}`}
                  className="border border-[var(--kr-line)] p-8 lg:p-10 group hover:border-[var(--kr-gold)]/40 transition-colors duration-500"
                >
                  <div className="flex items-start gap-6 mb-6">
                    <img
                      src={f.photo}
                      alt={f.name}
                      className="w-24 h-24 object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      style={{ filter: "contrast(1.05)" }}
                      loading="lazy"
                    />
                    <div>
                      <h3 className="font-serif-display text-[1.55rem] text-[var(--kr-text)] mb-2 group-hover:text-[var(--kr-gold)] transition-colors">
                        {f.name}
                      </h3>
                      <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-3">
                        {f.role}
                      </div>
                      <div className="text-[13px] text-[var(--kr-text-muted)] leading-[1.6]">
                        {f.focus}
                      </div>
                    </div>
                  </div>
                  <div className="kr-rule mb-4" />
                  <Link
                    to="/partnership"
                    className="kr-link font-mono-label text-[11px]"
                  >
                    Read the full biography →
                  </Link>
                </article>
              ))}
            </div>

            {/* Tier 2 — Partners */}
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Partners
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>
            <div className="grid md:grid-cols-2 gap-10">
              {PARTNERS.map((f) => (
                <article
                  key={f.id}
                  data-testid={`${LANDING.teamPagePartnerPrefix}${f.id}`}
                  className="border border-[var(--kr-line)] p-8 lg:p-10 group hover:border-[var(--kr-gold)]/40 transition-colors duration-500"
                >
                  <div className="flex items-start gap-6 mb-6">
                    <img
                      src={f.photo}
                      alt={f.name}
                      className="w-24 h-24 object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      style={{ filter: "contrast(1.05)" }}
                      loading="lazy"
                    />
                    <div>
                      <h3 className="font-serif-display text-[1.55rem] text-[var(--kr-text)] mb-2 group-hover:text-[var(--kr-gold)] transition-colors">
                        {f.name}
                      </h3>
                      <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-3">
                        {f.role}
                      </div>
                      <div className="text-[13px] text-[var(--kr-text-muted)] leading-[1.6]">
                        {f.focus}
                      </div>
                    </div>
                  </div>
                  <div className="kr-rule mb-4" />
                  <Link
                    to="/partnership"
                    className="kr-link font-mono-label text-[11px]"
                  >
                    Read the full biography →
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Leadership — compact cards (executive tier, below Partners) */}
        <section
          id="leadership"
          data-testid="kr-team-leadership"
          className="py-20 lg:py-28 border-t border-[var(--kr-line-soft)] scroll-mt-32"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Leadership
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>

            <div className="grid gap-10 md:grid-cols-2">
              {LEADERSHIP.map((m) => (
                <article
                  key={m.id}
                  id={m.id}
                  data-testid={`kr-team-leadership-${m.id}`}
                  className={`border border-[var(--kr-line)] p-8 lg:p-10 group hover:border-[var(--kr-gold)]/40 transition-colors duration-500 scroll-mt-32 ${
                    LEADERSHIP.length === 1
                      ? "md:col-span-2 md:max-w-[calc(50%-1.25rem)] md:mx-auto md:w-full"
                      : ""
                  }`}
                >
                  <div className="flex items-start gap-6 mb-6">
                    <img
                      src={m.photo}
                      alt={m.name}
                      className="w-24 h-24 object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      style={{ filter: "contrast(1.05)" }}
                      loading="lazy"
                    />
                    <div>
                      <h3 className="font-serif-display text-[1.55rem] text-[var(--kr-text)] mb-2 group-hover:text-[var(--kr-gold)] transition-colors">
                        {m.name}
                      </h3>
                      <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-3">
                        {m.role}
                      </div>
                      <div className="text-[13px] text-[var(--kr-text-muted)] leading-[1.6]">
                        {m.focus}
                      </div>
                    </div>
                  </div>
                  <div className="kr-rule mb-4" />
                  <Link
                    to={`/leadership#${m.id}`}
                    data-testid={`kr-team-leadership-readmore-${m.id}`}
                    className="kr-link font-mono-label text-[11px]"
                  >
                    Read the full biography →
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Regional Coverage — compact cards (on-the-ground representation) */}
        <section
          id="regional-coverage"
          data-testid="kr-team-regional-coverage"
          className="py-20 lg:py-28 border-t border-[var(--kr-line-soft)] scroll-mt-32"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Regional Coverage
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>

            <div className="grid gap-10 md:grid-cols-2">
              {REGIONAL_COVERAGE.map((m) => (
                <article
                  key={m.id}
                  id={m.id}
                  data-testid={`kr-team-regional-${m.id}`}
                  className={`border border-[var(--kr-line)] p-8 lg:p-10 group hover:border-[var(--kr-gold)]/40 transition-colors duration-500 scroll-mt-32 ${
                    REGIONAL_COVERAGE.length === 1
                      ? "md:col-span-2 md:max-w-[calc(50%-1.25rem)] md:mx-auto md:w-full"
                      : ""
                  }`}
                >
                  <div className="flex items-start gap-6 mb-6">
                    <img
                      src={m.photo}
                      alt={m.name}
                      className="w-24 h-24 object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      style={{ filter: "contrast(1.05)" }}
                      loading="lazy"
                    />
                    <div>
                      <h3 className="font-serif-display text-[1.55rem] text-[var(--kr-text)] mb-2 group-hover:text-[var(--kr-gold)] transition-colors">
                        {m.name}
                      </h3>
                      <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-3">
                        {m.role}
                      </div>
                      <div className="text-[13px] text-[var(--kr-text-muted)] leading-[1.6]">
                        {m.focus}
                      </div>
                    </div>
                  </div>
                  <div className="kr-rule mb-4" />
                  <Link
                    to={`/regional-coverage#${m.id}`}
                    data-testid={`kr-team-regional-readmore-${m.id}`}
                    className="kr-link font-mono-label text-[11px]"
                  >
                    Read the full biography →
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Senior Advisors — compact cards (matching partner flow) */}
        <section
          id="senior-advisors"
          data-testid="kr-team-senior-advisors"
          className="py-20 lg:py-28 border-t border-[var(--kr-line-soft)] scroll-mt-32"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            {/* Tier marker — matches Founding Partners / Partners pattern */}
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Senior Advisors
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>

            {/* Compact card grid — identical layout to Founding Partners / Partners */}
            <div className="grid md:grid-cols-2 gap-10">
              {SENIOR_ADVISORS.map((a) => (
                <article
                  key={a.id}
                  id={a.id}
                  data-testid={`kr-team-advisor-${a.id}`}
                  className="border border-[var(--kr-line)] p-8 lg:p-10 group hover:border-[var(--kr-gold)]/40 transition-colors duration-500 scroll-mt-32"
                >
                  <div className="flex items-start gap-6 mb-6">
                    <img
                      src={a.photo}
                      alt={a.name}
                      className="w-24 h-24 object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      style={{ filter: "contrast(1.05)" }}
                      loading="lazy"
                    />
                    <div>
                      <h3 className="font-serif-display text-[1.55rem] text-[var(--kr-text)] mb-2 group-hover:text-[var(--kr-gold)] transition-colors">
                        {a.name}
                      </h3>
                      <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-3">
                        {a.role} · {a.qualifier}
                      </div>
                      <div className="text-[13px] text-[var(--kr-text-muted)] leading-[1.6]">
                        {a.pillars.map((p) => p.value).join(" · ")}
                      </div>
                    </div>
                  </div>
                  <div className="kr-rule mb-4" />
                  <Link
                    to={`/advisors#${a.id}`}
                    data-testid={`kr-team-advisor-readmore-${a.id}`}
                    className="kr-link font-mono-label text-[11px]"
                  >
                    Read the full biography →
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Reserved seats */}
        <section className="py-20 lg:py-28 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 mb-12">
              <div className="lg:col-span-6">
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    Reserved Seats
                  </span>
                  <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
                </div>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.6rem,2.8vw,2.2rem)] leading-[1.2] max-w-[28ch]">
                  Senior appointments advanced on a{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    confidential
                  </span>{" "}
                  basis.
                </h2>
              </div>
              <div className="lg:col-span-6">
                <p className="text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] lg:pt-2">
                  The reserved senior seats described below complete the
                  founding partnership. Named biographies will be published as
                  appointments are finalised.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
              {RESERVED.map((r) => (
                <article
                  key={r.seal}
                  data-testid={`${LANDING.teamPageReservedPrefix}${r.seal}`}
                  className="p-8 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.03)] transition-colors duration-500"
                >
                  <div
                    className="w-12 h-12 border border-[var(--kr-gold)]/60 flex items-center justify-center font-serif-display text-[1.2rem] italic text-[var(--kr-gold)] mb-6"
                    aria-hidden
                  >
                    {r.seal}
                  </div>
                  <h3 className="font-serif-display text-[1.2rem] text-[var(--kr-text)] mb-4 leading-[1.25]">
                    {r.role}
                  </h3>
                  <p className="text-[var(--kr-text-muted)] text-[13.5px] leading-[1.7]">
                    {r.profile}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Network */}
        <section className="py-20 lg:py-28 border-t border-[var(--kr-line)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Committee · Network · Oversight
              </span>
              <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
            </div>
            <div className="space-y-0 border-t border-[var(--kr-line)]">
              {NETWORK.map((n, i) => (
                <article
                  key={n.title}
                  data-testid={`${LANDING.teamPageNetworkPrefix}${i}`}
                  className="grid lg:grid-cols-12 gap-10 lg:gap-16 py-12 border-b border-[var(--kr-line)]"
                >
                  <div className="lg:col-span-4">
                    <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-3">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-serif-display text-[var(--kr-text)] text-[1.8rem] leading-[1.2] mb-5">
                      {n.title}
                    </h3>
                    <p className="text-[var(--kr-text-muted)] text-[15px] leading-[1.8]">
                      {n.summary}
                    </p>
                  </div>
                  <div className="lg:col-span-8">
                    <ul className="space-y-3">
                      {n.members.map((m) => (
                        <li
                          key={m}
                          className="flex items-start gap-4 text-[var(--kr-text)] text-[14.5px] leading-[1.7] border-b border-[var(--kr-line-soft)] pb-3"
                        >
                          <span
                            aria-hidden
                            className="mt-[8px] block w-[5px] h-[5px] flex-shrink-0"
                            style={{
                              background: "var(--kr-gold)",
                              transform: "rotate(45deg)",
                            }}
                          />
                          <span>{m}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-[var(--kr-line)] py-20 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 flex flex-wrap gap-6 items-center justify-between">
            <h3 className="font-serif-display text-[var(--kr-text)] text-[1.7rem] leading-[1.2] max-w-[40ch]">
              Initiate a{" "}
              <span className="italic text-[var(--kr-gold)]">confidential</span>{" "}
              dialogue with the partnership.
            </h3>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.teamPageRequest}
                className="kr-btn-primary"
              >
                Request Investor Access
                <span aria-hidden>→</span>
              </button>
              <Link to="/partnership" className="kr-btn-ghost">
                Partnership Detail
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default TeamPage;
